import { useCallback, useRef, useEffect, useState } from "react";
import * as Tone from "tone";
import { Box, Button, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";


function Calibration() {
  const oscRef = useRef(null);
  const pannerRef = useRef(null);
  const volRef = useRef(null);
  const [currentFreq, setCurrentFreq] = useState(500);

  const [conversionTable, setConversionTable] = useState({
    500: [],
    1000: [],
    2000: [],
    4000: [],
    6000: [],
    8000: []
  });

  const handleTableChange = (row) => {
    setConversionTable(prev => {
      const tableCopy = { ...prev };
      const freqArray = [...tableCopy[currentFreq]];
      const existingEntry = freqArray.find(item => item.DbHL === row.DbHL);

      if (existingEntry) {
        existingEntry.DbSPL = row.DbSPL;
        existingEntry.DbFS = row.DbFS;
      } else {
        freqArray.push(row);
      }

      tableCopy[currentFreq] = freqArray;
      return tableCopy;
    });
  }

  const getConversionTable = () => {
    const table = [];
    for (let DbHL = -10; DbHL <= 90; DbHL += 5) {
      const row = conversionTable[currentFreq]?.find(item => item.DbHL === DbHL) ?? { DbHL, DbSPL: undefined, DbFS: undefined };
      table.push(<TableRow key={`${DbHL} dB HL @${currentFreq} Hz`}>
        <TableCell>{row.DbHL}</TableCell>
        <TableCell>
          <TextField value={row.DbSPL || ''} onChange={(e) => handleTableChange({ ...row, DbSPL: e.target.value })} />
        </TableCell>
        <TableCell>
          <TextField value={row.DbFS || ''} onChange={(e) => handleTableChange({ ...row, DbFS: e.target.value })} />
          <Button onClick={() => playPulse(row.DbFS)} id="play-sound-button">Play Tone</Button>
        </TableCell>
      </TableRow>);
    }
    return table;
  };

  useEffect(() => {
    const panner = new Tone.Panner(0).toDestination();
    const osc = new Tone.Oscillator(1000, "sine");
    const vol = new Tone.Volume(-Infinity).connect(panner);
    osc.connect(vol);
    osc.start();
    pannerRef.current = panner;
    oscRef.current = osc;
    volRef.current = vol;
    return () => {
      try {
        osc.stop();
        osc.disconnect();
        vol.disconnect();
        panner.disconnect();
      } catch { }
    };
  }, []);

  const playPulse = useCallback(
    async (DbHL) => {
      try {
        await Tone.start();
      } catch { }

      if (isNaN(DbHL)) {
        alert('You must enter a numerical value for dB FS');
        return;
      } else if (Number(DbHL) > 0) {
        alert('You must enter a value less than or equal to 0 for dB FS');
        return;
      }

      const osc = oscRef.current;
      const panner = pannerRef.current;
      const vol = volRef.current;
      const freq = currentFreq;

      // This value controls which ear it plays in. For the
      // calibration, it is hardcoded to the left ear.
      panner.pan.value = -1;
      osc.frequency.value = freq;
      vol.volume.value = DbHL;
      await new Promise((r) => setTimeout(r, 500));
      vol.volume.value = -Infinity;
    },
    [currentFreq]
  );

  const mapTableToConfig = () => {
    const config = {
      500: {},
      1000: {},
      2000: {},
      4000: {},
      6000: {},
      8000: {}
    };
    for (const freq of [500, 1000, 2000, 4000, 6000, 8000]) {
      for (let DbHL = -10; DbHL <= 90; DbHL += 5) {
        config[freq][DbHL] = conversionTable[freq].find(item => item.DbHL === DbHL)?.DbFS ?? null;
      }
    }
    return config;
  }

  const downloadFile = () => {
    const blob = new Blob([JSON.stringify(mapTableToConfig(), null, 2)], { type: 'text/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'headphoneConfig.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Box
      sx={{
        margin: "20",
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography>Calibration for headphones at</Typography>
        <Select id="freq" value={currentFreq} onChange={(e) => setCurrentFreq(e.target.value)}>
          <MenuItem value={500}>500</MenuItem>
          <MenuItem value={1000}>1000</MenuItem>
          <MenuItem value={2000}>2000</MenuItem>
          <MenuItem value={4000}>4000</MenuItem>
          <MenuItem value={6000}>6000</MenuItem>
          <MenuItem value={8000}>8000</MenuItem>
        </Select>
        <Typography>Hz</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>dB HL</TableCell>
              <TableCell sx={{ width: '40%' }}>dB SPL (This should be recorded by playing the dB HL at the current frequency from a known calibrated device, and then entering the value that appears on the sound meter)</TableCell>
              {/* The low end of the range for dB FS is dependent on the resolution of the system's digital to analog converter in bits. This number assumes a 16-bit resolution. */}
              <TableCell sx={{ width: '40%' }}>dB FS [-96 &#8804; value &#8804; 0]<Button onClick={() => downloadFile()} id="download-config-button">Download Current Configuration</Button></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getConversionTable()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Calibration;